/**
 * Deep navigation module for the progressive V1/V2 migration.
 *
 * User-visible copy is deliberately owned by the caller through `locale.text`.
 * The module only returns structured status and reason codes.
 */

import { createPilotControl } from "../pilot/createPilotControl";

const VALID_KINDS = new Set(["group", "destination"]);
const VALID_READINESS = new Set(["candidate", "parity-approved"]);
const VALID_VERSIONS = new Set(["v1", "v2"]);
const DEFAULT_RESULT_LIMIT = 20;
const NOOP_PILOT = createPilotControl({
  telemetry: Object.freeze({ append() {} }),
});

export class NavigationRegistryError extends Error {
  constructor(code, details = {}) {
    super(code);
    this.name = "NavigationRegistryError";
    this.code = code;
    this.details = details;
  }
}

const registryError = (code, details) => {
  throw new NavigationRegistryError(code, details);
};

const normalizePath = (pattern) => {
  if (typeof pattern !== "string" || pattern.trim() === "") {
    registryError("INVALID_ROUTE_PATTERN", { pattern });
  }

  const withoutQuery = pattern.trim();
  const withLeadingSlash = withoutQuery.startsWith("/")
    ? withoutQuery
    : `/${withoutQuery}`;
  return withLeadingSlash.length > 1
    ? withLeadingSlash.replace(/\/+$/, "")
    : withLeadingSlash;
};

const safeDecode = (value) => {
  try {
    return decodeURIComponent(value);
  } catch (_) {
    return value;
  }
};

const compilePattern = (pattern) => {
  const normalized = normalizePath(pattern);
  const segments = normalized === "/" ? [] : normalized.slice(1).split("/");
  const parameterNames = [];
  const requiredParameterNames = [];
  let score = 0;
  let signature = "";
  let source = "^";

  if (segments.length === 0) {
    source += "/";
    signature = "/";
  }

  segments.forEach((segment) => {
    if (!segment.startsWith(":")) {
      const escaped = segment.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
      source += `/${escaped}`;
      signature += `/${segment.toLowerCase()}`;
      score += 10;
      return;
    }

    const optional = segment.endsWith("?");
    const name = segment.slice(1, optional ? -1 : undefined);
    if (!name || !/^[A-Za-z][A-Za-z0-9_]*$/.test(name)) {
      registryError("INVALID_ROUTE_PATTERN", { pattern });
    }
    if (parameterNames.includes(name)) {
      registryError("DUPLICATE_ROUTE_PARAMETER", { pattern, name });
    }

    parameterNames.push(name);
    if (!optional) requiredParameterNames.push(name);
    source += optional ? "(?:/([^/]+))?" : "/([^/]+)";
    signature += optional ? "/:optional" : "/:required";
    score += optional ? 1 : 5;
  });

  source += "/?$";
  const regex = new RegExp(source, "i");

  return Object.freeze({
    normalized,
    parameterNames: Object.freeze(parameterNames),
    requiredParameterNames: Object.freeze(requiredParameterNames),
    regex,
    score: score + segments.length,
    signature,
    segments: Object.freeze(segments),
  });
};

const normalizeKeyList = (value, field, nodeId) => {
  if (value === undefined) return Object.freeze([]);
  if (!Array.isArray(value) || value.some((item) => typeof item !== "string" || !item)) {
    registryError("INVALID_CONTEXT_KEYS", { field, nodeId });
  }
  return Object.freeze([...new Set(value)]);
};

const compileVersion = (definition, version, nodeId) => {
  if (!definition || typeof definition !== "object") {
    registryError("INVALID_ROUTE_DEFINITION", { nodeId, version });
  }

  if (
    version === "v2" &&
    !VALID_READINESS.has(definition.readiness)
  ) {
    registryError("INVALID_V2_READINESS", {
      nodeId,
      readiness: definition.readiness,
    });
  }

  const pattern = compilePattern(definition.pattern);
  const contextKeys = normalizeKeyList(
    definition.contextKeys,
    "contextKeys",
    nodeId
  );
  const requiredContextKeys = normalizeKeyList(
    definition.requiredContextKeys,
    "requiredContextKeys",
    nodeId
  );
  const allowedKeys = new Set(contextKeys);

  pattern.parameterNames.forEach((key) => allowedKeys.add(key));
  requiredContextKeys.forEach((key) => {
    if (!allowedKeys.has(key)) {
      registryError("REQUIRED_CONTEXT_NOT_ALLOWED", { nodeId, version, key });
    }
  });

  return Object.freeze({
    contextKeys: Object.freeze([...allowedKeys]),
    pattern,
    readiness: version === "v2" ? definition.readiness : undefined,
    requiredContextKeys,
  });
};

const compileRegistry = (registry) => {
  if (!Array.isArray(registry) || registry.length === 0) {
    registryError("EMPTY_REGISTRY");
  }

  const nodes = new Map();

  registry.forEach((rawNode, index) => {
    if (!rawNode || typeof rawNode !== "object") {
      registryError("INVALID_NODE", { index });
    }
    if (!VALID_KINDS.has(rawNode.kind)) {
      registryError("INVALID_NODE_KIND", { id: rawNode.id, kind: rawNode.kind });
    }
    if (typeof rawNode.id !== "string" || rawNode.id.trim() === "") {
      registryError("INVALID_NODE_ID", { index });
    }
    if (nodes.has(rawNode.id)) {
      registryError("DUPLICATE_NODE", { id: rawNode.id });
    }
    if (typeof rawNode.labelKey !== "string" || rawNode.labelKey.trim() === "") {
      registryError("INVALID_MESSAGE_KEY", { id: rawNode.id });
    }

    const base = {
      id: rawNode.id,
      kind: rawNode.kind,
      parentId: rawNode.parentId || null,
      order: Number.isFinite(rawNode.order) ? rawNode.order : index,
      labelKey: rawNode.labelKey,
      keywordKeys: normalizeKeyList(rawNode.keywordKeys, "keywordKeys", rawNode.id),
      menu: rawNode.menu === true,
      searchShortcut: rawNode.searchShortcut === true,
      icon: typeof rawNode.icon === "string" ? rawNode.icon : undefined,
      audience: typeof rawNode.audience === "string" ? rawNode.audience : undefined,
    };

    if (rawNode.kind === "group") {
      nodes.set(rawNode.id, Object.freeze({ ...base, versions: null }));
      return;
    }

    if (typeof rawNode.accessKey !== "string" || rawNode.accessKey.trim() === "") {
      registryError("INVALID_ACCESS_KEY", { id: rawNode.id });
    }
    const accessKeysAnyOf = rawNode.accessKeysAnyOf === undefined
      ? [rawNode.accessKey]
      : rawNode.accessKeysAnyOf;
    if (
      !Array.isArray(accessKeysAnyOf) ||
      accessKeysAnyOf.length === 0 ||
      accessKeysAnyOf.some(
        (key) => typeof key !== "string" || key.trim() === ""
      ) ||
      new Set(accessKeysAnyOf).size !== accessKeysAnyOf.length ||
      !accessKeysAnyOf.includes(rawNode.accessKey)
    ) {
      registryError("INVALID_ACCESS_POLICY", { id: rawNode.id });
    }
    // Une page propre à la V2 déclare sa seule route V2 ; toutes les autres
    // gardent leur route V1 comme référence.
    if (!rawNode.versions || (!rawNode.versions.v1 && !(rawNode.v2Only && rawNode.versions.v2))) {
      registryError("MISSING_V1_ROUTE", { id: rawNode.id });
    }

    nodes.set(
      rawNode.id,
      Object.freeze({
        ...base,
        accessKey: rawNode.accessKey,
        accessKeysAnyOf: Object.freeze([...new Set(accessKeysAnyOf)]),
        versions: Object.freeze({
          v1: rawNode.versions.v1 ? compileVersion(rawNode.versions.v1, "v1", rawNode.id) : null,
          v2: rawNode.versions.v2
            ? compileVersion(rawNode.versions.v2, "v2", rawNode.id)
            : null,
        }),
      })
    );
  });

  nodes.forEach((node) => {
    if (node.parentId && !nodes.has(node.parentId)) {
      registryError("UNKNOWN_PARENT", { id: node.id, parentId: node.parentId });
    }
  });

  const visits = new Map();
  const visit = (node) => {
    const state = visits.get(node.id);
    if (state === "visiting") registryError("PARENT_CYCLE", { id: node.id });
    if (state === "visited") return;
    visits.set(node.id, "visiting");
    if (node.parentId) visit(nodes.get(node.parentId));
    visits.set(node.id, "visited");
  };
  nodes.forEach(visit);

  const routes = [];
  const signatures = new Map();
  nodes.forEach((node) => {
    if (node.kind !== "destination") return;
    ["v1", "v2"].forEach((version) => {
      const route = node.versions[version];
      if (!route) return;
      const collisionKey = route.pattern.signature;
      const existing = signatures.get(collisionKey);
      if (existing) {
        registryError("ROUTE_COLLISION", {
          id: node.id,
          version,
          pattern: route.pattern.normalized,
          collidesWith: existing,
        });
      }
      signatures.set(collisionKey, {
        id: node.id,
        version,
        pattern: route.pattern.normalized,
      });
      routes.push(Object.freeze({ node, route, version }));
    });
  });

  routes.sort((left, right) => right.route.pattern.score - left.route.pattern.score);
  const orderedNodes = [...nodes.values()].sort(
    (left, right) => left.order - right.order || left.id.localeCompare(right.id)
  );

  return Object.freeze({
    nodes,
    orderedNodes: Object.freeze(orderedNodes),
    routes: Object.freeze(routes),
  });
};

const normalizeSearchText = (value) =>
  String(value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim();

const validContextValue = (value) => {
  if (typeof value === "string") return true;
  return (
    Array.isArray(value) &&
    value.length > 0 &&
    value.every((item) => typeof item === "string")
  );
};

const sanitizeContext = (context, route) => {
  const source = context && typeof context === "object" ? context : {};
  const sanitized = {};
  route.contextKeys.forEach((key) => {
    if (validContextValue(source[key])) sanitized[key] = source[key];
  });
  return Object.freeze(sanitized);
};

const missingRequiredContext = (context, route) =>
  route.requiredContextKeys.filter((key) => !validContextValue(context[key]));

const matchLocation = (compiled, location) => {
  const pathname = normalizePath(location && location.pathname ? location.pathname : "/");

  for (const candidate of compiled.routes) {
    const match = candidate.route.pattern.regex.exec(pathname);
    if (!match) continue;

    const rawContext = {};
    candidate.route.pattern.parameterNames.forEach((name, index) => {
      if (match[index + 1] !== undefined) {
        rawContext[name] = safeDecode(match[index + 1]);
      }
    });

    const params = new URLSearchParams((location && location.search) || "");
    candidate.route.contextKeys.forEach((key) => {
      if (rawContext[key] !== undefined) return;
      const values = params.getAll(key);
      if (values.length === 1) rawContext[key] = values[0];
      if (values.length > 1) rawContext[key] = values;
    });

    const state = location?.state && typeof location.state === "object"
      ? location.state
      : {};
    candidate.route.contextKeys.forEach((key) => {
      if (rawContext[key] === undefined && validContextValue(state[key])) {
        rawContext[key] = state[key];
      }
    });

    return {
      context: sanitizeContext(rawContext, candidate.route),
      node: candidate.node,
      route: candidate.route,
      version: candidate.version,
    };
  }

  return null;
};

// Un lien vers une page de liste porte l'état de la liste dans son adresse
// (recherche, filtres, tri… — règle 11) : des paramètres que le registre de
// navigation ne déclare pas. Quand l'adresse demandée mène à la même page,
// ils sont gardés au lieu d'être réduits aux seules clés de contexte.
const keepLocationState = (href, location) => {
  if (typeof href !== "string" || !location?.search) return href;
  const queryIndex = href.indexOf("?");
  const pathname = queryIndex >= 0 ? href.slice(0, queryIndex) : href;
  if (normalizePath(pathname) !== normalizePath(location.pathname || "")) return href;
  const params = new URLSearchParams(queryIndex >= 0 ? href.slice(queryIndex + 1) : "");
  new URLSearchParams(location.search).forEach((value, key) => {
    if (!params.has(key)) params.append(key, value);
  });
  const search = params.toString();
  return search ? `${pathname}?${search}` : pathname;
};

const buildHref = (route, context) => {
  const sanitized = sanitizeContext(context, route);
  const missing = missingRequiredContext(sanitized, route);
  if (missing.length > 0) return { href: null, missing };

  const usedKeys = new Set();
  const pathSegments = [];
  route.pattern.segments.forEach((segment) => {
    if (!segment.startsWith(":")) {
      pathSegments.push(segment);
      return;
    }
    const optional = segment.endsWith("?");
    const key = segment.slice(1, optional ? -1 : undefined);
    const value = sanitized[key];
    if (!validContextValue(value)) {
      if (!optional) missing.push(key);
      return;
    }
    usedKeys.add(key);
    const scalar = Array.isArray(value) ? value[0] : value;
    pathSegments.push(encodeURIComponent(scalar));
  });

  if (missing.length > 0) return { href: null, missing };

  const params = new URLSearchParams();
  route.contextKeys.forEach((key) => {
    if (usedKeys.has(key) || !validContextValue(sanitized[key])) return;
    const values = Array.isArray(sanitized[key])
      ? sanitized[key]
      : [sanitized[key]];
    // `new` is a V2 path sentinel, never a legacy document id. When the peer
    // route carries the id in its query, omitting the sentinel restores the
    // trained V1 creation contract instead of loading a document named "new".
    if (key.endsWith("Id") && values.every((value) => value === "new")) return;
    values.forEach((value) => params.append(key, value));
  });

  const pathname = pathSegments.length > 0 ? `/${pathSegments.join("/")}` : "/";
  const search = params.toString();
  return { href: search ? `${pathname}?${search}` : pathname, missing: [] };
};

const setHas = (value, key) => {
  if (value instanceof Set) return value.has(key);
  if (Array.isArray(value)) return value.includes(key);
  if (value && typeof value === "object") return value[key] === true;
  return false;
};

const canAccess = (node, session) => {
  if (!session || node.kind !== "destination") return false;
  const accessKeys = node.accessKeysAnyOf || [node.accessKey];
  try {
    if (typeof session.canAccess === "function") {
      return accessKeys.some((accessKey) => session.canAccess(accessKey) === true);
    }
  } catch (_) {
    return false;
  }
  return accessKeys.some((accessKey) => setHas(session.accessKeys, accessKey));
};

const ancestorsOf = (compiled, node) => {
  const ancestors = [];
  let current = node.parentId ? compiled.nodes.get(node.parentId) : null;
  while (current) {
    ancestors.unshift(current);
    current = current.parentId ? compiled.nodes.get(current.parentId) : null;
  }
  return ancestors;
};

const validateDependencies = (router, preferences, pilot) => {
  if (!router || typeof router.commit !== "function") {
    throw new TypeError("router.commit must be a function");
  }
  if (
    !preferences ||
    typeof preferences.read !== "function" ||
    typeof preferences.write !== "function"
  ) {
    throw new TypeError("preferences.read and preferences.write must be functions");
  }
  if (
    !pilot ||
    typeof pilot.decide !== "function" ||
    typeof pilot.record !== "function"
  ) {
    throw new TypeError("pilot.decide and pilot.record must be functions");
  }
};

/**
 * @param {{
 *   registry: readonly object[],
 *   router: { commit(input: {href: string, mode: "push"|"replace"}): Promise<void>|void },
 *   preferences: { read(userId: string): Promise<"v1"|"v2">|"v1"|"v2", write(userId: string, version: "v1"|"v2"): Promise<void>|void },
 *   pilot?: { decide(input: object): {version: "v1"|"v2", reason: string}, record(event: object): Promise<object> }
 * }} config
 */
export const createNavigationModule = ({
  registry,
  router,
  preferences,
  pilot = NOOP_PILOT,
}) => {
  validateDependencies(router, preferences, pilot);
  const compiled = compileRegistry(registry);
  const moduleToken = {};
  const recoveredAttempts = new WeakSet();
  const recoveringAttempts = new WeakSet();
  const localeIndexes = new Map();

  const recordPilot = (event) => {
    try {
      const pending = pilot.record(event);
      if (pending && typeof pending.catch === "function") pending.catch(() => {});
    } catch (_) {
      // Telemetry is deliberately best-effort and must never affect navigation.
    }
  };

  const decidePilot = (input) => {
    try {
      const decision = pilot.decide(input);
      if (
        !decision ||
        !VALID_VERSIONS.has(decision.version) ||
        typeof decision.reason !== "string" ||
        !decision.reason
      ) {
        return { version: "v1", reason: "pilot-policy" };
      }
      return decision;
    } catch (_) {
      return { version: "v1", reason: "pilot-policy" };
    }
  };

  const preferenceFor = async (session) => {
    if (!session || typeof session.userId !== "string" || !session.userId) {
      return "v1";
    }
    try {
      const preference = await preferences.read(session.userId);
      return VALID_VERSIONS.has(preference) ? preference : "v1";
    } catch (_) {
      return "v1";
    }
  };

  const resolve = ({ node, context, session, requestedVersion, preferredVersion }) => {
    if (!node || node.kind !== "destination") {
      return { status: "not-found" };
    }
    if (!canAccess(node, session)) return { status: "forbidden" };

    const pilotDecision = decidePilot({
      destinationId: node.id,
      preferredVersion,
      readiness: node.versions.v2?.readiness,
      requestedVersion,
      rollout: session && session.rollout,
    });
    let { reason, version } = pilotDecision;
    let resolutionContext = context;

    // Page propre à la V2 : elle n'existe pas en V1, donc elle n'apparaît ni
    // dans le menu ni dans la bascule d'interface de qui utilise encore la V1.
    if (!node.versions.v1) {
      if (version !== "v2" && requestedVersion !== "v2") return { status: "not-found", reason: "v2-only" };
      version = "v2";
      reason = "v2-only";
    }

    if (version === "v2") {
      let v2Context = sanitizeContext(resolutionContext, node.versions.v2);
      const missingV2 = missingRequiredContext(v2Context, node.versions.v2);
      const v1Required = new Set(node.versions.v1?.requiredContextKeys || []);
      const createSentinelKeys = missingV2.filter((key) => (
        node.versions.v2.pattern.parameterNames.includes(key) && !v1Required.has(key)
      ));
      if (missingV2.length > 0 && createSentinelKeys.length === missingV2.length) {
        resolutionContext = {
          ...(resolutionContext || {}),
          ...Object.fromEntries(createSentinelKeys.map((key) => [key, "new"])),
        };
        v2Context = sanitizeContext(resolutionContext, node.versions.v2);
      }
      if (missingRequiredContext(v2Context, node.versions.v2).length > 0) {
        version = "v1";
        reason = "v2-context-incompatible";
      }
    }

    const route = node.versions[version];
    const compatibleContext = sanitizeContext(resolutionContext, route);
    const built = buildHref(route, compatibleContext);
    if (!built.href) {
      return {
        status: "not-found",
        reason: "missing-context",
        missingContextKeys: built.missing,
      };
    }

    return {
      status: "ready",
      context: compatibleContext,
      destinationId: node.id,
      href: built.href,
      node,
      reason,
      version,
    };
  };

  const createAttempt = (decision) =>
    Object.freeze({
      __navigationModule: moduleToken,
      context: decision.context,
      destinationId: decision.destinationId,
      href: decision.href,
      version: decision.version,
    });

  const buildLocaleIndex = (locale) => {
    if (!locale || typeof locale.cacheKey !== "string" || typeof locale.text !== "function") {
      throw new TypeError("locale.cacheKey and locale.text are required");
    }
    if (localeIndexes.has(locale.cacheKey)) return localeIndexes.get(locale.cacheKey);

    const index = new Map();
    compiled.orderedNodes.forEach((node) => {
      const label = String(locale.text(node.labelKey));
      const keywords = node.keywordKeys.map((key) => String(locale.text(key)));
      index.set(
        node.id,
        Object.freeze({
          keywords: Object.freeze(keywords),
          label,
          normalized: normalizeSearchText([label, ...keywords].join(" ")),
        })
      );
    });
    localeIndexes.set(locale.cacheKey, index);
    return index;
  };

  const projectDestination = (decision, localeIndex) => ({
    id: decision.node.id,
    label: localeIndex.get(decision.node.id).label,
    href: decision.href,
    version: decision.version,
    icon: decision.node.icon,
  });

  const projectMenu = (session, preferredVersion, localeIndex) => {
    const projected = new Map();

    compiled.orderedNodes.forEach((node) => {
      if (node.kind === "group") {
        projected.set(node.id, {
          id: node.id,
          kind: "group",
          label: localeIndex.get(node.id).label,
          icon: node.icon,
          ...(node.audience ? { audience: node.audience } : {}),
          order: node.order,
          children: [],
        });
        return;
      }
      if (!node.menu || !canAccess(node, session)) return;
      const decision = resolve({
        node,
        context: {},
        session,
        requestedVersion: "auto",
        preferredVersion,
      });
      if (decision.status !== "ready") return;
      projected.set(node.id, {
        ...projectDestination(decision, localeIndex),
        kind: "destination",
        order: node.order,
      });
    });

    compiled.orderedNodes.forEach((node) => {
      const item = projected.get(node.id);
      if (!item || !node.parentId) return;
      let parentNode = compiled.nodes.get(node.parentId);
      while (parentNode && parentNode.kind !== "group") {
        parentNode = parentNode.parentId
          ? compiled.nodes.get(parentNode.parentId)
          : null;
      }
      const parent = parentNode ? projected.get(parentNode.id) : null;
      if (parent && parent.kind === "group") parent.children.push(item);
    });

    const hasDestination = (item) =>
      item.kind === "destination" || item.children.some(hasDestination);
    const cloneVisible = (item) => {
      if (item.kind === "destination") return Object.freeze({ ...item });
      const children = item.children
        .filter(hasDestination)
        .map(cloneVisible);
      return Object.freeze({ ...item, children: Object.freeze(children) });
    };

    return Object.freeze(
      compiled.orderedNodes
        .filter((node) => !node.parentId)
        .map((node) => projected.get(node.id))
        .filter(Boolean)
        .filter(hasDestination)
        .map(cloneVisible)
    );
  };

  const projectBreadcrumbs = (
    current,
    session,
    preferredVersion,
    localeIndex
  ) => {
    if (!current || !canAccess(current.node, session)) return Object.freeze([]);
    const chain = [...ancestorsOf(compiled, current.node), current.node].filter(
      (node) => node.kind === "group" || canAccess(node, session)
    );
    return Object.freeze(
      chain.map((node, index) => {
        if (node.kind === "group") {
          return Object.freeze({
            id: node.id,
            label: localeIndex.get(node.id).label,
            href: null,
            current: false,
          });
        }
        const ownRequiredContext = new Set(
          Object.values(node.versions).filter(Boolean).flatMap(
            (version) => version.requiredContextKeys
          )
        );
        const descendantRequiredContext = new Set(
          chain.slice(index + 1).flatMap((descendant) => (
            descendant.kind === "destination"
              ? Object.values(descendant.versions).filter(Boolean).flatMap(
                  (version) => version.requiredContextKeys
                )
              : []
          ))
        );
        const breadcrumbContext = { ...current.context };
        descendantRequiredContext.forEach((key) => {
          if (!ownRequiredContext.has(key)) delete breadcrumbContext[key];
        });
        const decision = resolve({
          node,
          context: breadcrumbContext,
          session,
          requestedVersion: "auto",
          preferredVersion,
        });
        return Object.freeze({
          id: node.id,
          label: localeIndex.get(node.id).label,
          href: decision.status === "ready" ? decision.href : null,
          current: index === chain.length - 1,
        });
      })
    );
  };

  const projectSearch = ({
    session,
    preferredVersion,
    localeIndex,
    query,
    limit,
  }) => {
    const normalizedQuery = normalizeSearchText(query);
    const terms = normalizedQuery.split(/\s+/).filter(Boolean);
    const results = [];

    compiled.orderedNodes.forEach((node) => {
      const isSearchable = node.menu || (terms.length > 0 && node.searchShortcut);
      if (node.kind !== "destination" || !isSearchable || !canAccess(node, session)) return;
      const local = localeIndex.get(node.id);
      const parentText = ancestorsOf(compiled, node)
        .map((ancestor) => localeIndex.get(ancestor.id).label)
        .join(" ");
      const haystack = normalizeSearchText(`${local.normalized} ${parentText}`);
      if (!terms.every((term) => haystack.includes(term))) return;

      const decision = resolve({
        node,
        context: {},
        session,
        requestedVersion: "auto",
        preferredVersion,
      });
      if (decision.status !== "ready") return;

      const normalizedLabel = normalizeSearchText(local.label);
      let rank = 3;
      if (normalizedLabel === normalizedQuery) rank = 0;
      else if (normalizedLabel.startsWith(normalizedQuery)) rank = 1;
      else if (local.normalized.includes(normalizedQuery)) rank = 2;
      results.push({
        ...projectDestination(decision, localeIndex),
        rank,
        order: node.order,
      });
    });

    results.sort(
      (left, right) =>
        left.rank - right.rank ||
        left.order - right.order ||
        left.id.localeCompare(right.id)
    );
    return Object.freeze(
      results.slice(0, limit).map(({ rank, order, ...result }) => Object.freeze(result))
    );
  };

  /**
   * Builds the coherent shell projection for a location and viewer.
   */
  const read = async ({
    location,
    session,
    locale,
    query = "",
    resultLimit = DEFAULT_RESULT_LIMIT,
  }) => {
    const preferredVersion = await preferenceFor(session);
    const localeIndex = buildLocaleIndex(locale);
    const current = matchLocation(compiled, location);
    let route = { status: "not-found" };

    if (current) {
      if (!canAccess(current.node, session)) {
        route = { status: "forbidden", reason: "permission" };
      } else if (
        missingRequiredContext(current.context, current.route).length > 0
      ) {
        route = { status: "not-found", reason: "missing-context" };
      } else {
        const currentDecision = {
          context: current.context,
          destinationId: current.node.id,
          href: buildHref(current.route, current.context).href,
          node: current.node,
          version: current.version,
        };
        route = {
          status: "ready",
          destinationId: current.node.id,
          version: current.version,
          ...(current.version === "v2"
            ? { readiness: current.route.readiness }
            : {}),
          // Une fiche : page V2 dont le chemin porte l'identifiant d'un objet
          // (`/v2/individuals/:animalId`). Le shell y montre la fiche d'origine.
          ...(current.version === "v2" && current.route.pattern.requiredParameterNames.length > 0
            ? { record: true, pathname: normalizePath(location?.pathname || "") }
            : {}),
          context: current.context,
          attempt: createAttempt(currentDecision),
        };
      }
    }

    // The physical route version remains meaningful when access or required
    // context fails. Dropping it made a forbidden `/v2/...` URL look like V1
    // in the persistent shell while its V2 route component was still mounted.
    // A V2 page kept outside the registry (it has no V1 peer) still belongs
    // to the V2 shell: its menu and search links must stay in V2.
    const active = current?.version
      || (/^\/v2(?:\/|$)/i.test(location?.pathname || "") ? "v2" : null);
    const switchDecision = route.status === "ready"
      ? resolve({
          node: current.node,
          context: current.context,
          session,
          requestedVersion: active === "v2" ? "v1" : "v2",
          preferredVersion,
        })
      : null;
    const directSwitchAvailable = Boolean(
      switchDecision &&
      switchDecision.status === "ready" &&
      switchDecision.version !== active
    );
    // A version switch projects the current destination. It must never send
    // the user to an unrelated home page when the peer is unavailable.
    const effectiveSwitchDecision = directSwitchAvailable ? switchDecision : null;
    const automaticDecision = route.status === "ready"
      ? resolve({
          node: current.node,
          context: current.context,
          session,
          requestedVersion: "auto",
          preferredVersion,
        })
      : null;

    // Entering a V2 route directly is itself a strong local intent: keep all
    // candidate menu/search targets in V2 for the rest of that shell session.
    // On V1 routes the stored preference still wins, preserving explicit
    // switches and existing deep-link behaviour.
    const projectionVersion = active === "v2" ? "v2" : preferredVersion;
    const safeLimit = Number.isInteger(resultLimit) && resultLimit > 0
      ? resultLimit
      : DEFAULT_RESULT_LIMIT;

    return Object.freeze({
      profileId: session?.userId || null,
      route: Object.freeze(route),
      menu: projectMenu(session, projectionVersion, localeIndex),
      breadcrumbs: projectBreadcrumbs(
        current,
        session,
        projectionVersion,
        localeIndex
      ),
      searchResults: projectSearch({
        session,
        preferredVersion: projectionVersion,
        localeIndex,
        query,
        limit: safeLimit,
      }),
      version: Object.freeze({
        active,
        preferred: preferredVersion,
        reason:
          automaticDecision && automaticDecision.status === "ready"
            ? automaticDecision.reason
            : automaticDecision && automaticDecision.reason
              ? automaticDecision.reason
              : route.status === "forbidden"
                ? "permission"
                : null,
        resolvedTarget:
          automaticDecision && automaticDecision.status === "ready"
            ? Object.freeze({
                destinationId: automaticDecision.destinationId,
                version: automaticDecision.version,
                href: automaticDecision.href,
              })
            : null,
        switchTarget:
          effectiveSwitchDecision
            ? Object.freeze({
                destinationId: effectiveSwitchDecision.destinationId,
                version: effectiveSwitchDecision.version,
                href: effectiveSwitchDecision.href,
              })
            : null,
      }),
    });
  };

  /**
   * Resolves and commits navigation through the configured router adapter.
   */
  const open = async ({
    to,
    session,
    requestedVersion = "auto",
    rememberVersion = false,
    history = "push",
    state,
    editing = "clean",
    discardConfirmed = false,
    sourceVersion,
  }) => {
    if (
      requestedVersion !== "auto" &&
      requestedVersion !== "v1" &&
      requestedVersion !== "v2"
    ) {
      return Object.freeze({ status: "not-found", reason: "invalid-version" });
    }
    const isManualSwitch = rememberVersion === true &&
      VALID_VERSIONS.has(requestedVersion) &&
      VALID_VERSIONS.has(sourceVersion) &&
      sourceVersion !== requestedVersion;
    const destinationId = to && typeof to.destinationId === "string"
      ? to.destinationId
      : null;
    const recordSwitch = (outcome, reason) => {
      if (!isManualSwitch || !destinationId) return;
      recordPilot({
        type: "version-switch",
        destinationId,
        fromVersion: sourceVersion,
        toVersion: requestedVersion,
        outcome,
        ...(reason ? { reason } : {}),
      });
    };

    if (editing === "dirty" && discardConfirmed !== true) {
      recordSwitch("blocked", "dirty");
      return Object.freeze({ status: "blocked", reason: "dirty" });
    }

    let node = null;
    let context = {};
    if (to && typeof to.destinationId === "string") {
      node = compiled.nodes.get(to.destinationId) || null;
      context = to.context || {};
    } else if (to && to.location) {
      const matched = matchLocation(compiled, to.location);
      if (matched) {
        node = matched.node;
        context = matched.context;
      }
    }

    const preferredVersion = await preferenceFor(session);
    const decision = resolve({
      node,
      context,
      session,
      requestedVersion,
      preferredVersion,
    });
    if (decision.status !== "ready") return Object.freeze(decision);

    const desiredV2 = requestedVersion === "v2" ||
      (requestedVersion === "auto" && preferredVersion === "v2");

    // Persist an explicit interface choice before changing the URL. Otherwise
    // the destination tree can mount with the previous preference and briefly
    // start a competing V1/V2 resolution. A failed storage write therefore
    // leaves both the current route and the current interface untouched.
    let preferenceStatus = "unchanged";
    if (
      rememberVersion === true &&
      (requestedVersion === "v1" || requestedVersion === "v2")
    ) {
      try {
        await preferences.write(session.userId, requestedVersion);
        preferenceStatus = "saved";
      } catch (_) {
        recordSwitch("failed", "preference-save-failed");
        return Object.freeze({
          status: "failed",
          reason: "preference-save-failed",
        });
      }
    }

    try {
      const crossesInterfaceGeneration = VALID_VERSIONS.has(sourceVersion) &&
        sourceVersion !== decision.version;
      const replace = history === "replace" || history === "document-replace";
      const mode = crossesInterfaceGeneration
        ? replace ? "document-replace" : "document-push"
        : replace
          ? history
          : history === "document-push"
            ? "document-push"
            : "push";
      await router.commit({
        href: to && to.location ? keepLocationState(decision.href, to.location) : decision.href,
        mode,
        ...(state !== undefined ? { state } : {}),
      });
    } catch (_) {
      if (preferenceStatus === "saved") {
        try {
          await preferences.write(session.userId, preferredVersion);
        } catch (_) {
          // The route did not change. A best-effort rollback must never mask
          // the router failure or start a second navigation.
        }
      }
      recordPilot({
        type: "error",
        destinationId: decision.destinationId,
        version: decision.version,
        phase: "navigation",
        reason: "router",
      });
      recordSwitch("failed", "router");
      return Object.freeze({ status: "failed", reason: "router" });
    }

    if (desiredV2 && decision.version === "v1") {
      recordPilot({
        type: "fallback",
        destinationId: decision.destinationId,
        reason: decision.reason,
      });
    }

    recordSwitch(
      decision.version === requestedVersion ? "navigated" : "blocked",
      decision.version !== requestedVersion
        ? decision.reason
        : undefined
    );

    return Object.freeze({
      status: "navigated",
      destinationId: decision.destinationId,
      version: decision.version,
      context: decision.context,
      href: decision.href,
      fallbackReason:
        (requestedVersion === "v2" ||
          (requestedVersion === "auto" && preferredVersion === "v2")) &&
        decision.version === "v1"
          ? decision.reason
          : undefined,
      preferenceStatus,
      attempt: createAttempt(decision),
    });
  };

  /**
   * Replaces a failed pre-render V2 attempt with its V1 equivalent once.
   */
  const recover = async ({ attempt, phase }) => {
    if (!attempt || attempt.__navigationModule !== moduleToken) {
      return Object.freeze({ status: "not-recoverable", reason: "invalid-attempt" });
    }
    if (attempt.version !== "v2") {
      return Object.freeze({ status: "not-recoverable", reason: "already-v1" });
    }
    if (phase !== "load" && phase !== "before-render") {
      return Object.freeze({ status: "not-recoverable", reason: "after-render" });
    }
    if (recoveredAttempts.has(attempt) || recoveringAttempts.has(attempt)) {
      return Object.freeze({ status: "not-recoverable", reason: "already-recovered" });
    }

    const node = compiled.nodes.get(attempt.destinationId);
    const context = sanitizeContext(attempt.context, node.versions.v1);
    const built = buildHref(node.versions.v1, context);
    if (!built.href) {
      return Object.freeze({ status: "not-recoverable", reason: "missing-context" });
    }

    recoveringAttempts.add(attempt);
    try {
      // L'arrivée en V1 porte la trace du repli : la restauration du choix
      // « Nouvelle interface » (useWelfareNavigation) ne renvoie pas aussitôt
      // vers la V2 qui vient d'échouer, sinon repli et restauration bouclent.
      await router.commit({
        href: built.href,
        mode: "replace",
        state: { welfareRecovery: { destinationId: attempt.destinationId, phase } },
      });
      recoveringAttempts.delete(attempt);
      recoveredAttempts.add(attempt);
      recordPilot({
        type: "fallback",
        destinationId: attempt.destinationId,
        reason: `pre-render-${phase}`,
      });
      return Object.freeze({
        status: "recovered",
        version: "v1",
        href: built.href,
        context,
      });
    } catch (_) {
      recoveringAttempts.delete(attempt);
      recordPilot({
        type: "error",
        destinationId: attempt.destinationId,
        version: "v2",
        phase: "recovery",
        reason: "router",
      });
      return Object.freeze({ status: "failed", reason: "router" });
    }
  };

  return Object.freeze({ read, open, recover });
};
